import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaFilter,
  FaInfoCircle,
  FaLeaf,
  FaPlus,
  FaPen,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";
import { toast } from "react-toastify";

import myConfig from "../../configs/config";
import CardContent from "../../components/CardContent";
import CreateScheduleModal from "../PendingSchedules/CreateScheduleModal";
import ServiceOrderInfoButton from "../PendingSchedules/ServiceOrderInfoButton";
import ProductsModal from "../Operation/ProductsModal";
import ConfirmApplicationModal from "../Operation/ConfirmApplicationModal";
import ScheduledBadge, {
  getObsCardClass,
  ObservationExecutionInfo,
} from "../Operation/ScheduledBadge";
import {
  getObservationMapPeriod,
  hasExecutedPeriod,
} from "../../helpers/observationDateUtils";
import { serviceOrderTypeLabel } from "../../constants/serviceOrderTypes";
import { sortObservationsByRecency } from "../PendingSchedules/serviceOrderListSort";
import FieldAppHarvestLossOrderResults from "./FieldAppHarvestLossOrderResults";
import { ServiceOrderProductsWithCopy } from "../PendingSchedules/ServiceOrderProductsPanel";
import "../PendingSchedules/ServiceOrderProductsPanel.css";

function enrichObservation(obs) {
  return {
    ...obs,
    operationId: obs.operation,
    operationName: obs.operation_name || "",
  };
}

function observationOverlapsRange(obs, rangeStartMs, rangeEndMs) {
  if (rangeStartMs == null || rangeEndMs == null) return true;
  const { start, end } = getObservationMapPeriod(obs);
  const obsStart = new Date(start).getTime();
  const obsEnd = new Date(end).getTime();
  return obsStart <= rangeEndMs && obsEnd >= rangeStartMs;
}

export function focusGraphIndicesForPeriod(chartGraphData, startIso, endIso) {
  if (!chartGraphData || !chartGraphData.length || !startIso || !endIso) {
    return null;
  }

  const startMs = new Date(startIso).getTime();
  const endMs = new Date(endIso).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return null;
  }

  let startIndex = 0;
  let endIndex = chartGraphData.length - 1;

  for (let index = 0; index < chartGraphData.length; index += 1) {
    const pointMs = new Date(chartGraphData[index].time).getTime();
    if (pointMs >= startMs) {
      startIndex = index;
      break;
    }
  }

  for (let index = chartGraphData.length - 1; index >= 0; index -= 1) {
    const pointMs = new Date(chartGraphData[index].time).getTime();
    if (pointMs <= endMs) {
      endIndex = index;
      break;
    }
  }

  if (endIndex < startIndex) {
    return null;
  }

  return {
    startIndex,
    endIndex,
    startTimeMs: startMs,
    endTimeMs: endMs,
  };
}

export default function FieldAppServiceOrdersSection({
  farmId,
  selection,
  chartGraphData,
  onFocusGraphPeriod,
  focusServiceOrderId = null,
  expandHarvestLossSpreadsheet = false,
  onlyShowServiceOrderId = null,
  onFocusServiceOrderHandled,
  t,
}) {
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [confirmingObsId, setConfirmingObsId] = useState(null);
  const [prefillObservationDate, setPrefillObservationDate] = useState(null);
  const [filterDate, setFilterDate] = useState(null);
  const [newestFirst, setNewestFirst] = useState(true);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("client-token")}`,
    }),
    []
  );

  const periodRangeMs = useMemo(() => {
    if (!selection || !selection.dateFrom || !selection.dateTo) {
      return { start: null, end: null };
    }
    return {
      start: new Date(selection.dateFrom).getTime(),
      end: new Date(selection.dateTo).getTime(),
    };
  }, [selection]);

  const loadObservations = useCallback(async ({ silent = false } = {}) => {
    if (!farmId) return;
    if (!silent) setLoading(true);
    try {
      const [pendingResponse, completedResponse] = await Promise.all([
        axios.get(`${myConfig.API_URL}/observation/pending/`, {
          params: { farm_id: farmId },
          headers: authHeaders,
        }),
        axios.get(`${myConfig.API_URL}/observation/completed/`, {
          params: { farm_id: farmId },
          headers: authHeaders,
        }),
      ]);

      const merged = [
        ...(pendingResponse.data || []),
        ...(completedResponse.data || []),
      ].map(enrichObservation);

      const unique = [];
      const seen = new Set();
      merged.forEach((item) => {
        if (seen.has(item.id)) return;
        seen.add(item.id);
        unique.push(item);
      });

      setObservations(unique);
    } catch (error) {
      console.error(error);
      if (!silent) {
        toast.error(t("Erro ao carregar ordens de serviço."), {
          position: "bottom-right",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders, farmId, t]);

  useEffect(() => {
    loadObservations();
    const timer = window.setInterval(
      () => loadObservations({ silent: true }),
      3000
    );
    return () => window.clearInterval(timer);
  }, [loadObservations]);

  const visibleObservations = useMemo(() => {
    const targetId = onlyShowServiceOrderId || focusServiceOrderId;
    if (onlyShowServiceOrderId) {
      const focused = observations.find(
        (obs) => Number(obs.id) === Number(onlyShowServiceOrderId)
      );
      return focused ? [focused] : [];
    }

    const filtered = observations.filter((obs) =>
      observationOverlapsRange(obs, periodRangeMs.start, periodRangeMs.end)
    );
    if (!targetId) {
      return sortObservationsByRecency(filtered, newestFirst);
    }
    const focused = observations.find(
      (obs) => Number(obs.id) === Number(targetId)
    );
    if (!focused || filtered.some((obs) => obs.id === focused.id)) {
      return sortObservationsByRecency(filtered, newestFirst);
    }
    return sortObservationsByRecency([...filtered, focused], newestFirst);
  }, [
    focusServiceOrderId,
    observations,
    onlyShowServiceOrderId,
    newestFirst,
    periodRangeMs.end,
    periodRangeMs.start,
  ]);

  useEffect(() => {
    if (!focusServiceOrderId || loading) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const element = document.getElementById(
        `field-app-service-order-${focusServiceOrderId}`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (onFocusServiceOrderHandled) {
        onFocusServiceOrderHandled();
      }
    }, 280);
    return () => window.clearTimeout(timer);
  }, [focusServiceOrderId, loading, onFocusServiceOrderHandled, visibleObservations]);

  const handleFilterByObservation = useCallback(
    (obs) => {
      const period = getObservationMapPeriod(obs);
      if (onFocusGraphPeriod) {
        onFocusGraphPeriod(period.start, period.end);
      }
    },
    [onFocusGraphPeriod]
  );

  const handleAddObservation = async (data) => {
    try {
      await axios.post(
        `${myConfig.API_URL}/observation/`,
        {
          farm_id: parseInt(farmId, 10),
          service_type: data.service_type || "",
          start_date: data.start_date,
          end_date: data.end_date,
          products_used: data.products_used,
          description: data.description,
        },
        { headers: authHeaders }
      );
      await loadObservations();
      toast.success(t("Ordem de serviço adicionada com sucesso!"), {
        position: "bottom-right",
      });
    } catch (error) {
      toast.error(t("Erro ao adicionar ordem de serviço"), {
        position: "bottom-right",
      });
    }
  };

  const handleUpdateObservation = async (id, data) => {
    try {
      await axios.patch(`${myConfig.API_URL}/observation/${id}/`, data, {
        headers: authHeaders,
      });
      await loadObservations();
      toast.success(t("Ordem de serviço atualizada com sucesso!"), {
        position: "bottom-right",
      });
    } catch (error) {
      toast.error(t("Erro ao atualizar ordem de serviço"), {
        position: "bottom-right",
      });
    }
  };

  const handleDeleteObservation = async (id) => {
    if (!window.confirm(t("Deseja excluir esta ordem de serviço?"))) return;
    try {
      await axios.delete(`${myConfig.API_URL}/observation/${id}/`, {
        headers: authHeaders,
      });
      await loadObservations();
      toast.success(t("Ordem de serviço excluída com sucesso!"), {
        position: "bottom-right",
      });
    } catch (error) {
      toast.error(t("Erro ao excluir ordem de serviço"), {
        position: "bottom-right",
      });
    }
  };

  const handleConfirmObservation = (id) => {
    setConfirmingObsId(id);
  };

  const handleSubmitConfirmation = async (confirmedAt) => {
    if (!confirmingObsId) return;
    try {
      await axios.patch(
        `${myConfig.API_URL}/observation/${confirmingObsId}/`,
        { confirmed: true, confirmed_at: confirmedAt },
        { headers: authHeaders }
      );
      setConfirmingObsId(null);
      await loadObservations();
      toast.success(t("Serviço confirmado com sucesso!"), {
        position: "bottom-right",
      });
    } catch (error) {
      toast.error(t("Erro ao confirmar serviço"), {
        position: "bottom-right",
      });
    }
  };

  const openProductsModal = () => {
    setPrefillObservationDate(null);
    setFilterDate(null);
    setShowProductsModal(true);
  };

  return (
    <>
      <div className="col-xs-12 field-app-service-orders-wrap">
        <CardContent
          title={
            <>
              <FaLeaf className="mr-2" />
              {t("Ordens de serviço")}
            </>
          }
          description={t(
            "Ordens enviadas aos operadores e registros de produtos/descrição no período filtrado do App de Campo."
          )}
          borderColor="#fd7e14"
        >
          {!onlyShowServiceOrderId ? (
          <div className="field-app-service-orders-toolbar">
            <div className="field-app-service-orders-create-actions">
              <ServiceOrderInfoButton t={t} />
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={() => setShowCreateModal(true)}
              >
                <FaPlus style={{ marginRight: 6 }} />
                {t("Nova ordem")}
              </button>
            </div>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={openProductsModal}
            >
              <FaPen style={{ marginRight: 6 }} />
              {t("Produtos e observações")}
            </button>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={() => setNewestFirst((current) => !current)}
              title={
                newestFirst
                  ? t("Mais recentes em cima. Clique para inverter.")
                  : t("Mais antigas em cima. Clique para inverter.")
              }
            >
              {newestFirst ? (
                <FaSortAmountDown style={{ marginRight: 6 }} />
              ) : (
                <FaSortAmountUp style={{ marginRight: 6 }} />
              )}
              {newestFirst ? t("Mais recentes") : t("Mais antigas")}
            </button>
          </div>
          ) : null}

          {loading ? (
            <p>{t("Carregando ordens de serviço...")}</p>
          ) : visibleObservations.length > 0 ? (
            <div className="field-app-service-orders-list">
              {visibleObservations.map((obs) => (
                <div
                  key={obs.id}
                  id={`field-app-service-order-${obs.id}`}
                  className={`${getObsCardClass(obs)}${
                    focusServiceOrderId &&
                    Number(obs.id) === Number(focusServiceOrderId)
                      ? " field-app-service-order-card--focused"
                      : ""
                  }`}
                >
                  <button
                    type="button"
                    className="field-app-service-order-filter-btn"
                    onClick={() => handleFilterByObservation(obs)}
                    title={
                      hasExecutedPeriod(obs)
                        ? t("Ver rastreamento do período executado")
                        : t("Ver rastreamento do período solicitado")
                    }
                  >
                    <FaFilter />
                  </button>
                  {obs.service_type ? (
                    <div className="field-app-service-order-type-badge">
                      {serviceOrderTypeLabel(obs.service_type, t)}
                    </div>
                  ) : null}
                  {(obs.site_farm_name || obs.service_farm_names || obs.land_plot_names) && (
                    <div className="field-app-service-order-location">
                      {(obs.site_farm_name || obs.service_farm_names) ? (
                        <span>
                          <strong>{t("Fazenda")}:</strong>{" "}
                          {obs.service_farm_names || obs.site_farm_name}
                        </span>
                      ) : null}
                      {obs.land_plot_names ? (
                        <span>
                          <strong>{t("Talhões")}:</strong> {obs.land_plot_names}
                        </span>
                      ) : null}
                    </div>
                  )}
                  {obs.operationName ? (
                    <div className="field-app-service-order-title">
                      {obs.operationName}
                    </div>
                  ) : null}
                  <ScheduledBadge
                    obs={obs}
                    onConfirm={handleConfirmObservation}
                  />
                  {Array.isArray(obs.assigned_operators) &&
                  obs.assigned_operators.length > 0 ? (
                    <div className="field-app-service-order-operators">
                      {t("Enviado para")}:{" "}
                      {obs.assigned_operators
                        .map((operator) => operator.name)
                        .join(", ")}
                    </div>
                  ) : null}
                  <ServiceOrderProductsWithCopy obs={obs} t={t} />
                  <ObservationExecutionInfo obs={obs} t={t} compact />
                  {obs.description ? (
                    <p className="field-app-service-order-description">
                      {obs.description}
                    </p>
                  ) : null}
                  {obs.service_type === "monitoramento_perda_colheita" ? (
                    <FieldAppHarvestLossOrderResults
                      farmId={farmId}
                      observationId={obs.id}
                      serviceOrderPlan={obs.service_order_plan}
                      serviceOrderPlanPublished={obs.service_order_plan_published}
                      orderLabel={
                        obs.operationName
                          ? `${obs.operationName} (#${obs.id})`
                          : `#${obs.id}`
                      }
                      selection={selection}
                      defaultExpanded={
                        expandHarvestLossSpreadsheet &&
                        Number(obs.id) === Number(focusServiceOrderId)
                      }
                      t={t}
                    />
                  ) : null}
                  {obs.audio_message_url ? (
                    <div className="field-app-service-order-audio">
                      <strong>{t("Áudio")}:</strong>
                      <audio controls preload="metadata" src={obs.audio_message_url} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-info">
              <FaInfoCircle className="mr-2" />
              {t("Nenhuma ordem de serviço neste período filtrado.")}
            </div>
          )}
        </CardContent>
      </div>

      <CreateScheduleModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        farmId={farmId}
        onCreated={loadObservations}
        t={t}
      />

      <ProductsModal
        show={showProductsModal}
        onHide={() => setShowProductsModal(false)}
        observations={observations}
        onAddObservation={handleAddObservation}
        onUpdateObservation={handleUpdateObservation}
        onDeleteObservation={handleDeleteObservation}
        onConfirmObservation={handleConfirmObservation}
        prefillDate={prefillObservationDate}
        filterDate={filterDate}
        t={t}
      />

      <ConfirmApplicationModal
        show={!!confirmingObsId}
        onHide={() => setConfirmingObsId(null)}
        onConfirm={handleSubmitConfirmation}
        t={t}
      />
    </>
  );
}
